import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Sparkles } from 'lucide-react';

const integrations = [
  {
    name: 'HubSpot',
    status: 'live' as const,
    logo: '/logos/hubspot.svg',
    description: 'Bi-directional sync, full field mapping, deal & contact enrichment',
  },
  {
    name: 'Google Workspace',
    status: 'live' as const,
    logo: '/logos/google-workspace.svg',
    description: 'Calendar, Meet transcripts & Gmail thread analysis',
  },
  {
    name: 'Slack',
    status: 'live' as const,
    logo: '/logos/slack.svg',
    description: 'Deal alerts, coaching nudges & pipeline updates in your channels',
  },
  {
    name: 'Salesforce',
    status: 'live' as const,
    logo: '/logos/salesforce.svg',
    description: 'Full CRM sync with custom objects, flows & permission mapping',
  },
  {
    name: 'Microsoft 365',
    status: 'beta' as const,
    logo: '/logos/microsoft-365.svg',
    description: 'Teams meetings, Outlook emails & calendar — private beta',
  },
  {
    name: 'Zoom',
    status: 'live' as const,
    logo: '/logos/zoom.svg',
    description: 'Meeting recordings & transcript ingestion for deal signals',
  },
  {
    name: 'Pipedrive',
    status: 'live' as const,
    logo: '/logos/pipedrive.svg',
    description: 'Pipeline sync, activity logging & deal management',
  },
  {
    name: 'LinkedIn Sales Nav',
    status: 'coming' as const,
    logo: '/logos/linkedin.svg',
    description: 'Contact enrichment & relationship intelligence',
  },
];

export const IntegrationsShowcase = () => {
  const liveCount = integrations.filter(i => i.status === 'live').length;
  const betaCount = integrations.filter(i => i.status === 'beta').length;

  return (
    <section className="w-full py-20 md:py-32 bg-muted/30">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Integrations
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Plugs into your{' '}
            <span className="text-gradient-teal">existing stack.</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {liveCount} integrations live today, {betaCount} in beta. Live, beta and roadmap statuses are clearly labelled — no surprises.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {integrations.map((integration, index) => (
            <motion.div
              key={integration.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="group bg-card rounded-2xl border border-border p-5 hover:border-primary/30 hover:shadow-card transition-all duration-300 relative overflow-hidden"
            >
              {integration.status === 'beta' && (
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent text-accent-foreground text-xs font-medium">
                    <Sparkles className="w-3 h-3" />
                    Beta
                  </span>
                </div>
              )}
              {integration.status === 'coming' && (
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-medium">
                    <Clock className="w-3 h-3" />
                    Roadmap
                  </span>
                </div>
              )}
              {integration.status === 'live' && (
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    <CheckCircle2 className="w-3 h-3" />
                    Live
                  </span>
                </div>
              )}

              <img src={integration.logo} alt={`${integration.name} integration with NoQ5.ai`} className="w-8 h-8 object-contain mb-3" />
              <h3 className="text-base font-bold text-foreground mb-1">{integration.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{integration.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center text-sm text-foreground mt-8"
        >
          Need an integration we don't have yet?{' '}
          <a href="https://app.noq5.ai/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary font-semibold underline underline-offset-4 hover:text-primary/80 transition-colors">
            Tell us
          </a>{' '}
        . we ship fast.
        </motion.p>
      </div>
    </section>
  );
};
